import { useCallback, useEffect, useState } from 'react'
import {
  skillCheckpointService,
  type SkillCheckpointPrompt,
  type SkillCheckpointRespondResult,
  type SkillCheckpointResponseCode,
} from '../services/skillCheckpointService'

/** Mirrors useGoalReviewPrompts: polls open skill checkpoints, marks shown,
 *  and optimistically removes a prompt on respond. */
export function useSkillCheckpoints(pollIntervalMs = 60000) {
  const [prompts, setPrompts] = useState<SkillCheckpointPrompt[]>([])

  const refetch = useCallback(async () => {
    try {
      const { prompts: next } = await skillCheckpointService.getCheckpoints()
      setPrompts(next)
    } catch {
      // non-fatal
    }
  }, [])

  useEffect(() => {
    void refetch()
    const iv = window.setInterval(() => void refetch(), pollIntervalMs)
    return () => window.clearInterval(iv)
  }, [pollIntervalMs, refetch])

  // L8: don't dedupe on a hook-lifetime ref (a re-raised prompt keeps its id
  // and would never re-mark). The backend already no-ops when not 'available'.
  const markShown = useCallback(async (promptId: string) => {
    await skillCheckpointService.markShown(promptId)
  }, [])

  const respond = useCallback(
    async (
      promptId: string,
      code: SkillCheckpointResponseCode,
    ): Promise<SkillCheckpointRespondResult> => {
      const result = await skillCheckpointService.respond(promptId, code)
      // M6: only remove the card when the backend actually accepted it.
      if (result.success) {
        setPrompts(prev => prev.filter(p => p.id !== promptId))
      }
      return result
    },
    [],
  )

  return { prompts, refetch, markShown, respond }
}
